/* eslint-disable @typescript-eslint/no-unused-vars */
import { StatusBlock } from "screens/partials/ecommerce/components/blocks/status-block";
import PageHeader from "components/page-header";
import OrdersBlock from "screens/partials/ecommerce/components/blocks/orders-block";
import EarningBlock from "screens/partials/ecommerce/components/blocks/earning-block";
import { Box, ShoppingCart, TrendingUp, TrendingDown, Minus } from "lucide-react";
import RevenueBarChart from "./components/revenue-bar-chart";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import DashboardDropdown, { TIMEFRAME_OPTIONS } from "./components/dashboard-dropdown";
import RecentOrderTable from "./components/recent-order-table";
import VisitorsReportChart from "./components/visitors-report";
import { products } from "./components/data";
import Product from "./components/product";
import WidgetBg2Img from "images/all-img/widget-bg-2.png";
import { useUserData } from "store/user-data";
import React, { useEffect, useState } from "react";
import Loading from "components/loading";
import { getStatisticsAction, getAdvancedStatisticsAction, getPredictiveStatisticsAction, Statistics } from "action/stat-action";
import { useSessionControl } from "hooks/use-session-control";
import RetentionBlock from "./components/blocks/retention-block";
import GeoOverviewBlock from "./components/blocks/geo-overview-block";
import ChurnRiskList from "./components/blocks/churn-risk-list";
import RevenueForecastBlock from "./components/blocks/revenue-forecast-block";

import { useQuery } from "@tanstack/react-query";
import { getProductDataAction, myProductsKey } from "action/product-action";
import { getAdsSummaryAction, timeframeDays } from "action/ads-action";
import { useStoreLink } from "lib/store-link";
import { GetStartedCard, MetaAdsWidget, StoreLinkCard } from "./components/get-started";

const StorboltEcommerceComponent = () => {
  const { userData } = useUserData();
  const businessId = userData?.business_id;
  const { sessionControl } = useSessionControl();

  // Independent timeframe states
  const [topTimeframe, setTopTimeframe] = useState("all");
  const [revenueTimeframe, setRevenueTimeframe] = useState("28d");
  const [statsTimeframe, setStatsTimeframe] = useState("28d");
  const [visitorsTimeframe, setVisitorsTimeframe] = useState("28d");
  const [productsTimeframe, setProductsTimeframe] = useState("28d");
  const [adsTimeframe, setAdsTimeframe] = useState("28d");
  const storeLink = useStoreLink();

  // Query for Top Cards
  const { data: globalResponse, isLoading: isGlobalLoading } = useQuery({
    queryKey: ["statistics", businessId, topTimeframe],
    queryFn: async () => {
      const resp = await getStatisticsAction(topTimeframe);
      if (resp?.status === 401) sessionControl(resp.status);
      return resp;
    },
    enabled: !!businessId,
  });

  // Query for Statistics section
  const { data: statsResponse, isLoading: isStatsLoading } = useQuery({
    queryKey: ["statistics", businessId, statsTimeframe],
    queryFn: () => getStatisticsAction(statsTimeframe),
    enabled: !!businessId,
  });

  // Query for Best Selling Products section
  const { data: productsResponse, isLoading: isProductsLoading } = useQuery({
    queryKey: ["statistics", businessId, productsTimeframe],
    queryFn: () => getStatisticsAction(productsTimeframe),
    enabled: !!businessId,
  });

  // Query for Advanced Statistics
  const { data: advancedResponse } = useQuery({
    queryKey: ["advanced-statistics", businessId],
    queryFn: () => getAdvancedStatisticsAction(),
    enabled: !!businessId,
  });

  // Query for Predictive Statistics
  const { data: predictiveResponse } = useQuery({
    queryKey: ["predictive-statistics", businessId],
    queryFn: () => getPredictiveStatisticsAction(),
    enabled: !!businessId,
  });

  // For the 3-step checklist and the Meta Ads card.
  const { data: myProducts, isLoading: myProductsLoading } = useQuery({
    queryKey: myProductsKey(businessId),
    queryFn: () => getProductDataAction(),
    enabled: !!businessId,
  });
  const { data: adsResponse, isLoading: adsLoading } = useQuery({
    queryKey: ["ads-summary", businessId, adsTimeframe],
    queryFn: () => getAdsSummaryAction(timeframeDays(adsTimeframe)),
    enabled: !!businessId,
  });
  const adsSummary = adsResponse?.error ? null : adsResponse?.body;
  const hasProduct = Array.isArray(myProducts?.body) && myProducts!.body.length > 0;

  const globalData = globalResponse?.data;
  const statsData = statsResponse?.data;
  const productsData = productsResponse?.data;
  const advancedData = advancedResponse?.data;
  const predictiveData = predictiveResponse?.data;

  // Helper to get timeframe label
  const getTimeframeLabel = (value: string) => {
    return TIMEFRAME_OPTIONS.find((opt) => opt.value === value)?.label || "All Time";
  };

  const [greeting, setGreeting] = useState("");

  const handleGreeting = () => {
    const currentHour = new Date().getHours();
    if (currentHour < 12) setGreeting("Good Morning");
    else if (currentHour < 18) setGreeting("Good Afternoon");
    else setGreeting("Good Evening");
  };

  useEffect(() => {
    handleGreeting();
  }, []);

  const isLoading = isGlobalLoading;

  // Growth Logic
  const growthRate = globalData?.growth_rate || 0;
  const isPositive = growthRate > 0;
  const isNegative = growthRate < 0;

  const growthIcon = isPositive ? (
    <TrendingUp className="w-5 h-5 text-success" />
  ) : isNegative ? (
    <TrendingDown className="w-5 h-5 text-destructive" />
  ) : (
    <Minus className="w-5 h-5 text-primary" />
  );

  const growthColor = isPositive ? "#5b8a3e" : isNegative ? "#cf3f33" : "#6b7a3f";
  const growthBg = isPositive ? "bg-success/10" : isNegative ? "bg-destructive/10" : "bg-primary/10";

  return isLoading ? (
    <div className="h-screen">
      <Loading />
    </div>
  ) : (
    <div className="space-y-5 overflow-x-hidden">
      <PageHeader
        className="mb-1 md:mb-3"
        title={
          <>
            {greeting}, {userData?.business_name || userData?.username}
          </>
        }
        description="Your Storbolt Dashboard"
        actions={
          <div className="flex items-center gap-2">
            <span className="text-xs text-default-600">Row Summary Filter:</span>
            <DashboardDropdown value={topTimeframe} onChange={setTopTimeframe} />
          </div>
        }
      />
      <StoreLinkCard link={storeLink} />
      {!myProductsLoading && !adsLoading && (
        <GetStartedCard hasProduct={hasProduct} hasPaidCampaign={!!adsSummary?.has_paid_campaign} link={storeLink} />
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <StatusBlock
          title="Revenue"
          timeline={getTimeframeLabel(topTimeframe)}
          total={`₦${globalData?.total_revenue?.toLocaleString() || 0}`}
          iconWrapperClass="bg-info/10"
          chartColor="#6b7a3f"
          icon={<ShoppingCart className="w-5 h-5  text-info" />}
        />
        <StatusBlock
          title="Products Sold"
          timeline={getTimeframeLabel(topTimeframe)}
          total={globalData?.total_products_sold_count || 0}
          icon={<Box className="w-5 h-5 text-warning" />}
          iconWrapperClass="bg-warning/10"
          chartColor="#c97b4a"
        />
        <StatusBlock
          title="Growth"
          timeline="From Last Week"
          total={`${(growthRate * 100).toFixed(0)}%`}
          icon={growthIcon}
          iconWrapperClass={growthBg}
          chartColor={growthColor}
        />
      </div>

      <div className="grid grid-cols-12 gap-5">
        <div className="col-span-12 lg:col-span-5">
          <Card className="h-full">
            <CardHeader className="flex flex-row items-center gap-1">
              <CardTitle className="flex-1">Statistics</CardTitle>
              <DashboardDropdown value={statsTimeframe} onChange={setStatsTimeframe} />
            </CardHeader>
            <CardContent>
              {isStatsLoading ? <Loading /> : (
                <div className="grid grid-cols-2 gap-5">
                  <div className="col-span-2 md:col-span-1">
                    <OrdersBlock
                      title="Orders"
                      total={statsData?.total_orders || 0}
                      chartColor="#c97b4a"
                      className="border-none shadow-none bg-default-50 "
                    />
                  </div>
                  <div className="col-span-2 md:col-span-1">
                    <OrdersBlock
                      title="Conversion Rate"
                      total={`${statsData?.conversion_rate?.toFixed(1) || 0}%`}
                      chartColor="#6b7a3f"
                      chartType="line"
                      percentageContent={
                        <span className="text-primary">+2.5%</span>
                      }
                      className="border-none shadow-none bg-default-50 col-span-2 md:col-span-1"
                    />
                  </div>
                  <div className="col-span-2">
                    <EarningBlock
                      title="Average Order Value"
                      total={`₦${statsData?.average_order_value?.toLocaleString() || 0}`}
                      className="col-span-2 border-none shadow-none bg-default-50"
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        <div className="col-span-12 lg:col-span-7">
          <Card className="h-full">
            <CardHeader className="flex flex-row items-center gap-1">
              <CardTitle className="flex-1">Meta Ads Performance</CardTitle>
              <DashboardDropdown value={adsTimeframe} onChange={setAdsTimeframe} />
            </CardHeader>
            <CardContent>
              {adsLoading ? <Loading /> : (
                <MetaAdsWidget summary={adsSummary} timeframeLabel={getTimeframeLabel(adsTimeframe)} />
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center gap-1">
          <CardTitle className="flex-1">Revenue Report</CardTitle>
          <DashboardDropdown value={revenueTimeframe} onChange={setRevenueTimeframe} />
        </CardHeader>
        <CardContent className="pt-5">
          <RevenueBarChart height={360} timeframe={revenueTimeframe} />
        </CardContent>
      </Card>

      <div className="grid gap-5 w-full">
        <div className="col-span-12">
          <Card>
            <CardHeader className="flex flex-row items-center gap-1">
              <CardTitle className="flex-1">Visitors Report</CardTitle>
              <DashboardDropdown value={visitorsTimeframe} onChange={setVisitorsTimeframe} />
            </CardHeader>
            <CardContent>
              <VisitorsReportChart height={350} timeframe={visitorsTimeframe} />
            </CardContent>
          </Card>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5">
        <Card>
          <CardHeader className="flex flex-row items-center gap-1">
            <CardTitle className="flex-1">Best Selling Products</CardTitle>
            <DashboardDropdown value={productsTimeframe} onChange={setProductsTimeframe} />
          </CardHeader>
          <CardContent>
            {isProductsLoading ? <Loading /> : (
              productsData?.product_stats?.length === 0 ? (
                <div className="flex justify-center items-center py-10">
                  No Data Available
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-4">
                  {productsData?.product_stats?.map((product, index) => (
                    <Product key={index} product={product} />
                  ))}
                </div>
              )
            )}
          </CardContent>
        </Card>
      </div>

      {advancedData && predictiveData && (
        <div className="grid gap-5 w-full">
          <div className="flex items-center gap-2">
            <h2 className="font-display text-lg font-semibold text-default-900">Advanced Insights</h2>
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-sage text-sage-foreground">
              New
            </span>
          </div>
          <div
            className={`grid grid-cols-1 md:grid-cols-2 gap-5 ${
              predictiveData.seasonal_peaks?.length > 0 ? "lg:grid-cols-4" : "lg:grid-cols-3"
            }`}
          >
            <RetentionBlock percentage={advancedData.retention_rate} />
            <RevenueForecastBlock forecast={predictiveData.revenue_forecast} />
            {/* CLV Block using simple Card for now or reuse EarningBlock styled differently */}
            <div className="bg-primary/10 rounded-20px p-5 flex flex-col justify-between">
              <div>
                <div className="text-sm font-medium text-default-600 mb-1">Lifetime Value (CLV)</div>
                <div className="font-display text-2xl font-bold text-primary">₦{advancedData.customer_lifetime_value?.toLocaleString() ?? 0}</div>
              </div>
              <p className="text-xs text-default-500 mt-2">Est. value per customer</p>
            </div>
            {/* Seasonal alerts only show when the API has a real one. */}
            {predictiveData.seasonal_peaks?.length > 0 && (
              <div className="bg-warning/10 rounded-20px p-5">
                <div className="text-sm font-medium text-warning-600 mb-1">Seasonal Alert</div>
                <div className="text-lg font-bold text-warning-700">{predictiveData.seasonal_peaks[0].period}</div>
                <p className="text-xs text-warning-600">{predictiveData.seasonal_peaks[0].action}</p>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <GeoOverviewBlock cities={advancedData.geo_distribution?.visitor_cities || []} />
            <ChurnRiskList risks={predictiveData.churn_risk || []} />
          </div>
        </div>
      )}
    </div>
  );
};

export default StorboltEcommerceComponent;
